import { useEffect, useState, type ReactNode } from "react";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { API_ERRORS, type MinimumVersion } from "@shared/contracts";
import { Toaster } from "@ds/ui/sonner";
import { UpdateBanner } from "@/app/components/UpdateBanner";
import { UpdateGate } from "@/app/components/UpdateGate";
import { primeManifestFromCache } from "@/app/config/manifest";
import { installFailureNotifier } from "@/app/lib/announce";
import { onGlobalApiError } from "@/app/lib/api-client";
import { watchManifest } from "@/app/lib/manifest";
import { checkForUpdate, markBundleHealthy } from "@/app/lib/ota";
import { useScreenAwake } from "@/app/lib/screen-awake";
import { hydrateUnitSystem } from "@/app/lib/units";
import { versionVerdict, type VersionVerdict } from "@/app/lib/version";
import { identifyUser, initObservability, log } from "@/app/observability";
import { queryClient, setSessionLostHandler } from "@/app/query/query-client";
import { persistOptions } from "@/app/query/query-persistence";
import { useAuthStatus } from "@/shared/auth";
import { bindKeyboardHeight, splash, statusBar } from "@/app/platform";
import { verifySessionOrSignOut } from "@/shared/auth";

/**
 * EVERYTHING THAT MUST HAPPEN ONCE, AT START-UP, IN ONE PLACE.
 *
 * Order matters more than it looks: reporting is armed before anything can fail, the
 * session is restored before the router can ask whether anyone is signed in, and the splash
 * is only released after the first paint — so nobody sees the half-second where both
 * answers are still "unknown".
 */
export function AppProviders({ children }: { children: ReactNode }) {
  /**
   * THE SCREEN STAYS LIT WHILE THE APP IS IN FRONT — for the whole app, not just the clock.
   *
   * Here rather than on a screen because that is the whole point of the change: an athlete
   * reading their next set off the lift log has the same chalky hands and the same phone on the
   * same floor as one watching a metcon count down. See `app/lib/screen-awake.ts`.
   */
  useScreenAwake();

  const [verdict, setVerdict] = useState<VersionVerdict>("ok");
  const [versionInfo, setVersionInfo] = useState<MinimumVersion | undefined>();

  useEffect(() => {
    /**
     * "THIS BUNDLE BOOTED" — FIRST, AND BEHIND NOTHING.
     *
     * If this build is running a bundle that arrived over the air, the native side is
     * already counting: ten seconds from launch (`readyTimeout` in capacitor.config.ts) and
     * it reverts to the bundle compiled into the binary. Reaching this line IS the proof
     * that the bundle parsed and React mounted, so it is what cancels the timer.
     *
     * It sits above the manifest read rather than below it because everything below can
     * wait and this cannot: a line here that awaits a store read, a dynamic import or a
     * round trip would let a perfectly good update be rolled back for being slow on an old
     * phone. It also does the reverse check first — a bundle built for another environment
     * is reverted here instead of being marked healthy (see app/lib/ota.ts).
     *
     * On the web, and in any binary built before the update plugin existed, it returns
     * immediately having done nothing.
     */
    void markBundleHealthy();

    /**
     * THE LAST-KNOWN MANIFEST IS READ BEFORE REPORTING IS ARMED, and that ordering is the
     * only reason two of its values do anything at all.
     *
     * Sentry fixes both session-replay sample rates when it initialises and gives no way to
     * change them afterwards. Arming first would mean reading the built-in defaults on every
     * single launch, so a rate the service set would never take effect on any phone, ever.
     *
     * IT IS NOT A NETWORK CALL. One read from the device's own preference store, which is
     * why it can sit in front of this — nothing at start-up waits on the service, which is
     * the property the whole manifest depends on. It cannot reject: every failure inside it
     * ends on the permissive defaults.
     */
    void primeManifestFromCache().then(() => initObservability());

    /**
     * The query cache decides that a failure should be announced; this decides what that
     * looks like. Installed here rather than from inside a component, because a component
     * that registers in an effect also UNREGISTERS on unmount — and the version of this that
     * did leaves failures announced zero times for as long as it is unmounted.
     */
    installFailureNotifier();

    /**
     * A 401 does not sign anyone out on its own. The cache hands the question here, and the
     * auth layer asks the service whether the session is genuinely gone — a phone changing
     * cells, a proxy, or a race with a refresh all produce a single 401, and signing out on
     * one is the difference between an app that blips and an app that "logs me out at
     * random". Concurrent failures collapse into one check.
     */
    setSessionLostHandler(() => void verifySessionOrSignOut());

    /**
     * KILOS OR POUNDS, READ BACK BEFORE THE FIRST WEIGHT IS PAINTED. One read from the
     * device's own preference store, issued here beside everything else that happens once —
     * a screen that hydrated it in an effect would re-read it on every mount and would race
     * its own writes. It cannot reject: every failure inside it lands on metric.
     */
    void hydrateUnitSystem();

    splash.hideWhenReady();
    const unbindKeyboard = bindKeyboardHeight();
    const unbindStatusBar = statusBar.bindToColorScheme();

    return () => {
      unbindKeyboard();
      unbindStatusBar();
    };
  }, []);

  /**
   * WHAT THIS BUILD SHOULD BE DOING, ASKED AT LAUNCH AND AGAIN ON EVERY RETURN THAT IS FAR
   * ENOUGH APART.
   *
   * Its own effect rather than a line in the start-up block above, because unlike everything
   * there it does not happen once: `watchManifest` owns both the first ask and the clock
   * that decides whether a resume is worth another one (see app/lib/manifest.ts).
   *
   * ONLY THE VERSION FLOOR IS LIFTED INTO STATE HERE, and only because it decides which of
   * three screens renders. Everything else in the manifest is read where it is used —
   * copying it into a component would put the app back where it started, with a value that
   * cannot change until the next launch.
   */
  useEffect(
    () =>
      watchManifest((manifest) => {
        /**
         * THE WALL IS STICKY FOR THE PROCESS. A re-check that cannot reach the service
         * answers against the permissive floor on purpose — a launch on a train must not
         * become a wall — and letting that answer LOWER the verdict would do the reverse:
         * someone walled correctly at launch walks into a tunnel, comes back, and is
         * returned to an app whose screens cannot parse what the service sends.
         */
        const next = versionVerdict(manifest.versionFloor);
        setVerdict((current) => (current === "update-required" ? current : next));
        setVersionInfo(manifest.versionFloor);

        /**
         * THE OVER-THE-AIR CHECK HANGS OFF THIS AND NOTHING ELSE, and the coupling is the
         * design rather than convenience.
         *
         * This callback fires after every ATTEMPT to reach the service, successful or not —
         * which is the one moment at which "have we heard from the kill switch this
         * session?" has a definite answer. Given its own timer it would sooner or later run
         * on a cached `enabled: true` from yesterday, and a channel that keeps running on
         * yesterday's permission is not one that can be switched off. `checkForUpdate`
         * refuses on a stale manifest for exactly that reason.
         *
         * It also inherits the manifest's clock — one ask per hour of foreground returns —
         * so an update is looked for about as often as the setting that permits it changes.
         * Nothing is applied here: a bundle that passes every check is armed for the NEXT
         * cold launch, never swapped under someone mid-task.
         */
        void checkForUpdate();
      }),
    [],
  );

  useEffect(
    () =>
      onGlobalApiError((code) => {
        if (code === API_ERRORS.APP_TOO_OLD) setVerdict("update-required");
      }),
    [],
  );

  if (verdict === "update-required") return <UpdateGate info={versionInfo} />;

  return (
    /**
     * THE CACHE IS READ BACK OFF DISK BEFORE ANYTHING FETCHES.
     *
     * Same provider contract as before plus one behaviour: while the restore is in flight no
     * query fetches, so nothing races the hydrated copy and no screen paints an empty state
     * it is about to replace a millisecond later. Reading one entry out of the web view's
     * store is fast, and the splash is still up while it happens.
     *
     * A restore that fails throws away the stored copy and starts cold — the app is exactly
     * as it was before this change — so it is a line in the log rather than something
     * somebody has to fix.
     */
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={persistOptions}
      onError={() => log.warn("persisted query cache could not be restored; starting cold")}
    >
      <IdentifyReporter />
      {children}
      {/**
        * THE SOFT NUDGE, and it can only be reached here because the wall above returned
        * first — below the floor a dismissible notice is the wrong answer and the two are
        * never on screen together.
        */}
      {verdict === "update-recommended" && versionInfo && <UpdateBanner info={versionInfo} />}
      {/**
        * INSIDE the query provider and outside the shell, which is what a transient notice
        * needs: it outlives any screen, and every announcement in the app is rendered by
        * this one mount. Sonner puts its own list at `z-index: 999999999`, well clear of the
        * shell's `z-30` bottom nav, and the primitive lifts it above the tab bar, the home
        * indicator and the keyboard.
        */}
      <Toaster />
    </PersistQueryClientProvider>
  );
}

/**
 * Ties reports to whoever is signed in — an id only, never an email or a name (the scrubber
 * in app/observability is the second wall). Inside the provider because it reads the session
 * from the cache, which does not exist above it.
 */
function IdentifyReporter() {
  const { user } = useAuthStatus();
  useEffect(() => identifyUser(user?.id ?? null), [user?.id]);
  return null;
}
